import { useState } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CHECK-IN REPORT — DESKTOP (with Drill-Ins)
   Design system: Maghil Analytics Design Tokens v1
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", hover: "#F0F1F5",
  accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)", accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  cardBorder: "#E2E5EC", divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const DRILLABLE = ["checkins","guests","avgParty","cancels","waitTime","dinein"];
const DRILL_TITLES = {
  checkins: "Total Check-Ins", guests: "Total Guests", avgParty: "Avg Party Size",
  cancels: "Cancellations", waitTime: "Avg Wait Time", dinein: "Avg Dine-In Duration",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MOCK DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const MOCK = {
  kpis: [
    { key: "checkins",    label: "TOTAL CHECK-INS",    value: "284",   delta: 8.3,  icon: "how_to_reg" },
    { key: "guests",      label: "TOTAL GUESTS",       value: "712",   delta: 5.1,  icon: "groups" },
    { key: "avgParty",    label: "AVG PARTY SIZE",     value: "2.5",   delta: -0.2, icon: "person" },
    { key: "cancels",     label: "CANCELLATION RATE",  value: "12.3%", delta: -2.1, icon: "person_cancel", invertDelta: true },
    { key: "waitTime",    label: "AVG WAIT TIME",      value: "18 min",delta: -3.4, icon: "schedule", invertDelta: true },
    { key: "dinein",      label: "AVG DINE-IN",        value: "47 min",delta: 1.2,  icon: "restaurant" },
    { key: "peakHour",    label: "PEAK HOUR",          value: "7 PM",  delta: null,  icon: "trending_up" },
    { key: "totalCancel", label: "TOTAL CANCELLATIONS",value: "35",    delta: -6.2, icon: "block", invertDelta: true },
  ],
  hourly: [
    { h: "10A", checkins: 8, guests: 18 },{ h: "11A", checkins: 22, guests: 52 },{ h: "12P", checkins: 41, guests: 98 },
    { h: "1P", checkins: 38, guests: 88 },{ h: "2P", checkins: 19, guests: 42 },{ h: "3P", checkins: 10, guests: 22 },
    { h: "4P", checkins: 7, guests: 15 },{ h: "5P", checkins: 25, guests: 60 },{ h: "6P", checkins: 44, guests: 110 },
    { h: "7P", checkins: 52, guests: 134 },{ h: "8P", checkins: 46, guests: 118 },{ h: "9P", checkins: 30, guests: 72 },
    { h: "10P", checkins: 14, guests: 32 },
  ],
  partySize: [
    { size: "1", count: 18, wait: 8, dinein: 32, guests: 18 },
    { size: "2", count: 94, wait: 14, dinein: 42, guests: 188 },
    { size: "3", count: 52, wait: 18, dinein: 48, guests: 156 },
    { size: "4", count: 68, wait: 22, dinein: 55, guests: 272 },
    { size: "5", count: 26, wait: 28, dinein: 62, guests: 130 },
    { size: "6", count: 16, wait: 34, dinein: 68, guests: 96 },
    { size: "7+", count: 10, wait: 42, dinein: 78, guests: 80 },
  ],
  channels: [
    { name: "Host Added", value: 142, pct: 50, color: T.accent },
    { name: "Online", value: 62, pct: 22, color: T.green },
    { name: "Kiosk", value: 45, pct: 16, color: T.orange },
    { name: "Google", value: 35, pct: 12, color: "#6366F1" },
  ],
  cancelTrend: [
    { d: "Mon", cancels: 4, rate: 9.1 },{ d: "Tue", cancels: 6, rate: 11.5 },{ d: "Wed", cancels: 5, rate: 10.2 },
    { d: "Thu", cancels: 7, rate: 13.0 },{ d: "Fri", cancels: 10, rate: 14.1 },{ d: "Sat", cancels: 12, rate: 15.4 },
    { d: "Sun", cancels: 8, rate: 12.8 },
  ],
  heatmap: (() => {
    const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
    const hours = [10,11,12,13,14,15,16,17,18,19,20,21,22];
    const base = [
      [2,5,12,10,4,2,1,6,14,18,16,8,3],[3,6,14,12,5,3,1,7,16,20,18,10,4],
      [2,5,11,9,4,2,1,5,13,17,15,7,3],[3,7,15,13,6,3,2,8,18,22,20,12,5],
      [5,10,20,18,8,4,2,12,24,30,28,16,8],[6,12,24,22,10,5,3,14,28,36,32,20,10],
      [4,8,18,16,7,3,2,10,20,26,22,14,6],
    ];
    return { days, hours, data: base };
  })(),
  dailyCheckins: [
    { d: "Mon", checkins: 34, guests: 82 },{ d: "Tue", checkins: 38, guests: 90 },
    { d: "Wed", checkins: 36, guests: 84 },{ d: "Thu", checkins: 42, guests: 106 },
    { d: "Fri", checkins: 52, guests: 132 },{ d: "Sat", checkins: 56, guests: 142 },
    { d: "Sun", checkins: 48, guests: 120 },
  ],
  waitByDay: [
    { d: "Mon", wait: 14 },{ d: "Tue", wait: 16 },{ d: "Wed", wait: 15 },
    { d: "Thu", wait: 18 },{ d: "Fri", wait: 24 },{ d: "Sat", wait: 28 },{ d: "Sun", wait: 20 },
  ],
  dineinByDay: [
    { d: "Mon", dur: 42 },{ d: "Tue", dur: 44 },{ d: "Wed", dur: 43 },
    { d: "Thu", dur: 46 },{ d: "Fri", dur: 52 },{ d: "Sat", dur: 55 },{ d: "Sun", dur: 48 },
  ],
  cancelByChannel: [
    { name: "Host Added", cancels: 14, rate: 9.9 },{ name: "Online", cancels: 10, rate: 16.1 },
    { name: "Kiosk", cancels: 7, rate: 15.6 },{ name: "Google", cancels: 4, rate: 11.4 },
  ],
  cancelByHour: [
    { h: "11A", c: 1 },{ h: "12P", c: 3 },{ h: "1P", c: 2 },{ h: "5P", c: 2 },
    { h: "6P", c: 5 },{ h: "7P", c: 8 },{ h: "8P", c: 6 },{ h: "9P", c: 4 },{ h: "10P", c: 2 },
  ],
  dineinBySize: [
    { size: "1", duration: 32 },{ size: "2", duration: 42 },{ size: "3", duration: 48 },
    { size: "4", duration: 55 },{ size: "5", duration: 62 },{ size: "6", duration: 68 },
    { size: "7+", duration: 78 },
  ],
  hourlyTable: [
    { hour: "7:00 PM", checkins: 12, guests: 31, cancels: 2, dinein: 48 },
    { hour: "6:00 PM", checkins: 14, guests: 35, cancels: 1, dinein: 52 },
    { hour: "5:00 PM", checkins: 8, guests: 19, cancels: 1, dinein: 44 },
    { hour: "4:00 PM", checkins: 3, guests: 7, cancels: 0, dinein: 38 },
    { hour: "3:00 PM", checkins: 4, guests: 9, cancels: 0, dinein: 36 },
    { hour: "2:00 PM", checkins: 7, guests: 16, cancels: 1, dinein: 42 },
    { hour: "1:00 PM", checkins: 13, guests: 30, cancels: 2, dinein: 50 },
    { hour: "12:00 PM", checkins: 15, guests: 38, cancels: 2, dinein: 46 },
  ],
  guestVolumeTrend: [
    { d: "2/9",  guests: 462, checkins: 138 },{ d: "2/10", guests: 440, checkins: 130 },
    { d: "2/11", guests: 478, checkins: 144 },{ d: "2/12", guests: 455, checkins: 136 },
    { d: "2/13", guests: 430, checkins: 126 },{ d: "2/14", guests: 390, checkins: 110 },
    { d: "2/15", guests: 465, checkins: 140 },{ d: "2/16", guests: 448, checkins: 132 },
    { d: "2/17", guests: 420, checkins: 122 },{ d: "2/18", guests: 358, checkins: 102 },
    { d: "2/19", guests: 410, checkins: 118 },{ d: "2/20", guests: 442, checkins: 130 },
    { d: "2/21", guests: 468, checkins: 142 },{ d: "2/22", guests: 380, checkins: 108 },
    { d: "2/23", guests: 395, checkins: 114 },{ d: "2/24", guests: 370, checkins: 105 },
    { d: "2/25", guests: 408, checkins: 120 },{ d: "2/26", guests: 415, checkins: 122 },
    { d: "2/27", guests: 388, checkins: 112 },{ d: "2/28", guests: 365, checkins: 100 },
    { d: "3/1",  guests: 490, checkins: 148 },{ d: "3/2",  guests: 510, checkins: 155 },
    { d: "3/3",  guests: 485, checkins: 146 },{ d: "3/4",  guests: 468, checkins: 140 },
    { d: "3/5",  guests: 445, checkins: 132 },{ d: "3/6",  guests: 470, checkins: 142 },
    { d: "3/7",  guests: 505, checkins: 152 },{ d: "3/8",  guests: 460, checkins: 136 },
    { d: "3/9",  guests: 480, checkins: 145 },{ d: "3/10", guests: 465, checkins: 138 },
  ],
};

const NAV_GROUPS = [
  { label: "Sales", icon: "bar_chart", items: ["Sales Summary","Hourly Sales","Order Channels"] },
  { label: "Menu", icon: "restaurant_menu", items: ["Item Sales","Category Sales","Discounts & Offers"] },
  { label: "Team", icon: "groups", items: ["Staff Performance","Voids & Exceptions","Refunds"] },
  { label: "Payments", icon: "credit_card", items: ["Tender Types","EOD Reconciliation"] },
  { label: "Guests", icon: "how_to_reg", items: ["Check-In Analytics","Party & Wait Times"] },
  { label: "Locations", icon: "location_on", items: ["Location Benchmark"] },
];

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE DESKTOP COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */
function ChartCard({ title, subtitle, flex = 1, children, minHeight = 220 }: any) {
  return (
    <div style={{ flex, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, minHeight }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function DBarChart({ data, valueKey, labelKey, height = 160, color = T.accent, threshold, showValues = true }: any) {
  const maxV = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {data.map((d, i) => {
        const v = d[valueKey]; const h = maxV > 0 ? (v / maxV) * height : 0;
        const isPeak = v === maxV; const over = threshold && v > threshold;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            {showValues && <div style={{ fontSize: 9, fontWeight: 600, color: over ? T.accent : isPeak ? color : T.t3, marginBottom: 4 }}>{v}</div>}
            <div style={{ width: "100%", maxWidth: 24, height: h, borderRadius: "3px 3px 0 0", background: over ? T.accent : isPeak ? color : `${color}66` }}/>
            <div style={{ fontSize: 9, color: isPeak ? T.t1 : T.t3, fontWeight: isPeak ? 600 : 400, marginTop: 6 }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

function DHProgressList({ data, labelKey, valueKey, valueSuffix = "", color = T.accent, labelWidth = 90 }: any) {
  const maxV = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: T.t2, width: labelWidth }}>{d[labelKey]}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{d[valueKey]}{valueSuffix}</span>
          </div>
          <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${maxV > 0 ? (d[valueKey] / maxV) * 100 : 0}%`, borderRadius: 3, background: `${color}AA` }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function CompareCards({ items }: any) {
  return (
    <div style={{ display: "flex", gap: 16 }}>
      {items.map((it, i) => (
        <div key={i} style={{
          flex: 1, textAlign: "center", padding: "20px 16px",
          background: it.highlight ? it.softBg : T.hover, borderRadius: 14,
          border: `1px solid ${it.highlight ? `${it.accentColor}33` : T.cardBorder}`,
        }}>
          <div style={{ fontSize: 11, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>{it.label}</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: it.highlight ? it.accentColor : T.t1, marginTop: 6, letterSpacing: -0.5 }}>{it.value}</div>
          <div style={{ fontSize: 11, color: T.t3, marginTop: 4 }}>{it.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SummaryStrip({ items }: any) {
  return (
    <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
      {items.map((it, i) => (
        <div key={i} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "16px 20px", boxShadow: T.cardShadow, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>{it.label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: it.color || T.t1, marginTop: 6, letterSpacing: -0.5 }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   GUEST VOLUME TREND — Dual-line SVG chart (Guests + Check-Ins)
   ═══════════════════════════════════════════════════════════════════════════ */
function GuestVolumeTrend({ height = 200 }: any) {
  const data = MOCK.guestVolumeTrend;
  const n = data.length;
  const allVals = [...data.map(d => d.guests), ...data.map(d => d.checkins)];
  const maxV = Math.max(...allVals);
  const minV = 0;
  const pad = { top: 8, right: 12, bottom: 28, left: 42 };
  const w = 600;
  const chartW = w - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  const toX = (i: any) => pad.left + (i / (n - 1)) * chartW;
  const toY = (v: any) => pad.top + chartH - ((v - minV) / (maxV - minV)) * chartH;

  const guestLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.guests).toFixed(1)}`).join(" ");
  const checkinLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.checkins).toFixed(1)}`).join(" ");
  const areaPath = guestLine + ` L${toX(n-1).toFixed(1)},${(pad.top + chartH).toFixed(1)} L${toX(0).toFixed(1)},${(pad.top + chartH).toFixed(1)} Z`;

  const yTicks = [0, 150, 300, 450, 600].filter(v => v <= maxV + 50);
  const xLabelIndices = data.map((_, i) => i).filter(i => i % 5 === 0 || i === n - 1);

  return (
    <ChartCard title="Guest Volume Trend" subtitle="Daily guests & check-ins" flex={1} minHeight={0}>
      <svg width="100%" viewBox={`0 0 ${w} ${height}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="guestArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={T.accent} stopOpacity="0.01"/>
          </linearGradient>
        </defs>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.left} y1={toY(v)} x2={w - pad.right} y2={toY(v)} stroke={T.divider} strokeWidth="0.5"/>
            <text x={pad.left - 6} y={toY(v) + 3} textAnchor="end" fontSize="9" fill={T.t3} fontFamily={T.font}>{v}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#guestArea)"/>
        <path d={guestLine} fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d={checkinLine} fill="none" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 4"/>
        <circle cx={toX(n-1)} cy={toY(data[n-1].guests)} r="4" fill={T.accent} stroke="#fff" strokeWidth="2"/>
        <circle cx={toX(n-1)} cy={toY(data[n-1].checkins)} r="3.5" fill={T.green} stroke="#fff" strokeWidth="2"/>
        {xLabelIndices.map(i => (
          <text key={i} x={toX(i)} y={height - 4} textAnchor="middle" fontSize="9" fill={T.t3} fontFamily={T.font}>{data[i].d}</text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 20, marginTop: 8, justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 18, height: 3, background: T.accent, borderRadius: 1.5 }}/>
          <span style={{ fontSize: 11, color: T.t2 }}>Guests</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="18" height="3"><line x1="0" y1="1.5" x2="18" y2="1.5" stroke={T.green} strokeWidth="2" strokeDasharray="4 3"/></svg>
          <span style={{ fontSize: 11, color: T.t2 }}>Check-Ins</span>
        </div>
      </div>
    </ChartCard>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SIDEBAR
   ═══════════════════════════════════════════════════════════════════════════ */
function Sidebar({ expandedGroup, onToggleGroup }: any) {
  return (
    <div style={{ width: 250, minWidth: 250, height: "100vh", position: "sticky", top: 0, background: T.surface, borderRight: `1px solid ${T.divider}`, boxShadow: T.sidebarShadow, display: "flex", flexDirection: "column", fontFamily: T.font, overflow: "auto" }}>
      <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #D23A3B, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16 }}>M</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Maghil</div>
            <div style={{ fontSize: 10, color: T.t3, letterSpacing: 0.5 }}>ANALYTICS</div>
          </div>
        </div>
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.hover, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}>
          <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t3 }}>location_on</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, flex: 1 }}>Downtown Grill</span>
          <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>expand_more</span>
        </div>
      </div>
      <div style={{ flex: 1, padding: "0 8px" }}>
        {NAV_GROUPS.map(g => {
          const open = expandedGroup === g.label;
          return (
            <div key={g.label} style={{ marginBottom: 2 }}>
              <div onClick={() => onToggleGroup(g.label)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: open ? T.accentSoft : "transparent" }}>
                <span className="material-symbols-rounded" style={{ fontSize: 18, color: open ? T.accent : T.t3 }}>{g.icon}</span>
                <span style={{ fontSize: 12.5, fontWeight: open ? 600 : 400, color: open ? T.accent : T.t2, flex: 1 }}>{g.label}</span>
                <span className="material-symbols-rounded" style={{ fontSize: 14, color: open ? T.accent : T.t3, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>chevron_right</span>
              </div>
              {open && (
                <div style={{ paddingLeft: 22, paddingTop: 2 }}>
                  {g.items.map(item => {
                    const active = item === "Check-In Analytics";
                    return (
                      <div key={item} style={{ padding: "7px 10px", borderRadius: 5, fontSize: 12, cursor: "pointer", fontWeight: active ? 600 : 400, color: active ? T.accent : T.t2, background: active ? T.accentSoft : "transparent", borderLeft: active ? `2px solid ${T.accent}` : "2px solid transparent", marginBottom: 1 }}>{item}</div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ padding: "16px 18px", borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700 }}>JM</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>James M.</div>
          <div style={{ fontSize: 10, color: T.t3 }}>General Manager</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TOP BAR
   ═══════════════════════════════════════════════════════════════════════════ */
function TopBar({ datePeriod, onDatePeriodChange, drillKey, onBack }: any) {
  const periods = ["Today","Yesterday","7 Days","30 Days","This Month","Custom"];
  const isDrill = !!drillKey;
  return (
    <div style={{ height: 56, position: "sticky", top: 0, zIndex: 10, background: T.surface, borderBottom: `1px solid ${T.divider}`, boxShadow: T.topbarShadow, display: "flex", alignItems: "center", padding: "0 24px", fontFamily: T.font }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {isDrill && (
          <div onClick={onBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginRight: 4 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
          </div>
        )}
        <span style={{ fontSize: 12, color: T.t3 }}>Guests</span>
        <span style={{ fontSize: 12, color: T.t3 }}>›</span>
        {isDrill ? (
          <>
            <span onClick={onBack} style={{ fontSize: 12, color: T.accent, cursor: "pointer" }}>Check-In Analytics</span>
            <span style={{ fontSize: 12, color: T.t3 }}>›</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{DRILL_TITLES[drillKey]}</span>
          </>
        ) : (
          <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>Check-In Analytics</span>
        )}
      </div>
      <div style={{ flex: 1 }}/>
      <div style={{ display: "flex", gap: 4, marginRight: 16 }}>
        {periods.map(p => (
          <div key={p} onClick={() => onDatePeriodChange(p)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: "pointer", background: datePeriod === p ? T.accentSoft : "transparent", color: datePeriod === p ? T.accent : T.t2 }}>{p}</div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 12, fontSize: 11.5, color: T.t2 }}>
        <span>Compare</span>
        <div style={{ width: 32, height: 18, borderRadius: 9, background: T.hover, position: "relative", cursor: "pointer" }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: T.surface, border: `1px solid ${T.cardBorder}`, position: "absolute", top: 2, left: 2 }}/>
        </div>
      </div>
      <div style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11.5, color: T.t2, cursor: "pointer", background: T.surface, border: `1px solid ${T.cardBorder}` }}>
        <span className="material-symbols-rounded" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>download</span>Export
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI CARD
   ═══════════════════════════════════════════════════════════════════════════ */
function KPICard({ label, value, delta, icon, invertDelta, drillable, onTap }: any) {
  const isPositive = invertDelta ? delta < 0 : delta > 0;
  const deltaColor = delta === null ? T.t3 : isPositive ? T.green : T.red;
  const deltaText = delta === null ? "—" : `${isPositive ? "↑" : "↓"} ${Math.abs(delta).toFixed(1)}%`;
  const blurColor = delta === null ? T.hover : isPositive ? "rgba(22,163,74,0.06)" : "rgba(220,38,38,0.06)";
  return (
    <div onClick={onTap} style={{
      flex: 1, minWidth: 145, background: T.card,
      border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`,
      borderRadius: 14, padding: "18px 20px", boxShadow: T.cardShadow, position: "relative", overflow: "hidden",
      cursor: drillable ? "pointer" : "default", transition: "box-shadow 0.15s",
    }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: blurColor, filter: "blur(20px)" }}/>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, position: "relative" }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
        <span className="material-symbols-rounded" style={{ fontSize: 20, color: T.t3 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, position: "relative" }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4, position: "relative" }}>
        {deltaText}{delta !== null && <span style={{ fontWeight: 400, color: T.t3, marginLeft: 4 }}>vs prior period</span>}
      </div>
      {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", bottom: 12, right: 14, fontSize: 16, color: T.accent, opacity: 0.3 }}>chevron_right</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOURLY SUMMARY — SVG Grouped Bar Chart + Data Table
   ═══════════════════════════════════════════════════════════════════════════ */
function HourlySummaryChart() {
  const raw = MOCK.hourlyTable;
  const data = [...raw].reverse();
  const n = data.length;
  const maxGuests = Math.max(...data.map(d => d.guests));
  const maxDinein = Math.max(...data.map(d => d.dinein));

  const w = 700; const h = 240;
  const pad = { top: 16, right: 60, bottom: 32, left: 48 };
  const cW = w - pad.left - pad.right;
  const cH = h - pad.top - pad.bottom;
  const barGroupW = cW / n;
  const barW = 16;
  const gap = 3;

  const yTicks = [0, 10, 20, 30, 40];
  const yTicksRight = [0, 15, 30, 45, 60];

  const toX = (i: any) => pad.left + i * barGroupW + barGroupW / 2;
  const toYLeft = (v: any) => pad.top + cH - (v / maxGuests) * cH;
  const toYRight = (v: any) => pad.top + cH - (v / maxDinein) * cH;

  const dineinLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toYRight(d.dinein).toFixed(1)}`).join(" ");

  return (
    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3 }}>monitoring</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Hourly Check-In Summary</span>
          </div>
          <div style={{ fontSize: 11, color: T.t3, marginTop: 2, marginLeft: 24 }}>Aggregated by hour for the selected period</div>
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          {[
            { color: T.accent, label: "Guests", style: "solid" },
            { color: T.green, label: "Check-Ins", style: "solid" },
            { color: T.red, label: "Cancellations", style: "solid" },
            { color: T.orange, label: "Dine-In (min)", style: "line" },
          ].map((l, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              {l.style === "line" ? (
                <svg width="16" height="3"><line x1="0" y1="1.5" x2="16" y2="1.5" stroke={l.color} strokeWidth="2" strokeDasharray="4 2"/></svg>
              ) : (
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }}/>
              )}
              <span style={{ fontSize: 10, color: T.t2 }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
        {yTicks.map(v => {
          const y = toYLeft(v);
          return (
            <g key={v}>
              <line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke={T.divider} strokeWidth="0.5"/>
              <text x={pad.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill={T.t3} fontFamily={T.font}>{v}</text>
            </g>
          );
        })}
        {yTicksRight.map(v => {
          const y = toYRight(v);
          return <text key={v} x={w - pad.right + 8} y={y + 3} textAnchor="start" fontSize="9" fill={T.orange} fontFamily={T.font}>{v}m</text>;
        })}

        {data.map((d, i) => {
          const cx = toX(i);
          const gH = (d.guests / maxGuests) * cH;
          const ciH = (d.checkins / maxGuests) * cH;
          const caH = (d.cancels / maxGuests) * cH;

          return (
            <g key={i}>
              <rect x={cx - barW * 1.5 - gap} y={pad.top + cH - gH} width={barW} height={gH} rx={3} fill={`${T.accent}CC`}/>
              <rect x={cx - barW * 0.5} y={pad.top + cH - ciH} width={barW} height={ciH} rx={3} fill={`${T.green}CC`}/>
              <rect x={cx + barW * 0.5 + gap} y={pad.top + cH - caH} width={barW} height={Math.max(caH, d.cancels > 0 ? 3 : 0)} rx={3} fill={`${T.red}CC`}/>
              <text x={cx - barW - gap} y={pad.top + cH - gH - 4} textAnchor="middle" fontSize="8" fontWeight="600" fill={T.accent} fontFamily={T.font}>{d.guests}</text>
              <text x={cx} y={pad.top + cH - ciH - 4} textAnchor="middle" fontSize="8" fontWeight="600" fill={T.green} fontFamily={T.font}>{d.checkins}</text>
              {d.cancels > 0 && <text x={cx + barW + gap} y={pad.top + cH - caH - 4} textAnchor="middle" fontSize="8" fontWeight="600" fill={T.red} fontFamily={T.font}>{d.cancels}</text>}
              <text x={cx} y={h - 8} textAnchor="middle" fontSize="10" fill={T.t3} fontFamily={T.font}>{d.hour.replace(":00 ", " ")}</text>
            </g>
          );
        })}

        <path d={dineinLine} fill="none" stroke={T.orange} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 3"/>
        {data.map((d, i) => (
          <g key={`dot-${i}`}>
            <circle cx={toX(i)} cy={toYRight(d.dinein)} r="4" fill={T.orange} stroke="#fff" strokeWidth="2"/>
            <text x={toX(i)} y={toYRight(d.dinein) - 8} textAnchor="middle" fontSize="8" fontWeight="600" fill={T.orange} fontFamily={T.font}>{d.dinein}</text>
          </g>
        ))}
      </svg>

      <div style={{ marginTop: 16, borderTop: `1px solid ${T.divider}`, paddingTop: 12 }}>
        <div style={{ display: "flex", borderBottom: `1px solid ${T.divider}`, paddingBottom: 6, marginBottom: 2 }}>
          {["Hour","Check-Ins","Guests","Cancellations","Dine-In (min)"].map(c => (
            <div key={c} style={{ flex: 1, fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>{c}</div>
          ))}
        </div>
        {raw.map((r, ri) => (
          <div key={ri} style={{ display: "flex", padding: "7px 0", borderBottom: ri < raw.length - 1 ? `1px solid ${T.divider}` : "none" }}>
            <div style={{ flex: 1, fontSize: 11, fontWeight: 500, color: T.t1 }}>{r.hour}</div>
            <div style={{ flex: 1, fontSize: 11, color: T.t1 }}>{r.checkins}</div>
            <div style={{ flex: 1, fontSize: 11, color: T.t2 }}>{r.guests}</div>
            <div style={{ flex: 1 }}><span style={{ fontSize: 11, fontWeight: r.cancels > 0 ? 600 : 400, color: r.cancels > 0 ? T.red : T.t3 }}>{r.cancels}</span></div>
            <div style={{ flex: 1, fontSize: 11, color: T.t2 }}>{r.dinein}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD CONTENT
   ═══════════════════════════════════════════════════════════════════════════ */
function MainContent({ onDrill }: any) {
  const hData = MOCK.hourly;
  const pData = MOCK.partySize;
  const cData = MOCK.cancelTrend;
  const { days, hours, data: heat } = MOCK.heatmap;
  const maxHeat = Math.max(...heat.flat());
  const hourLabels = hours.map(h => h > 12 ? `${h-12}P` : h === 12 ? "12P" : `${h}A`);
  const chData = MOCK.channels;
  const chTotal = chData.reduce((s, c) => s + c.value, 0);
  const circ = 2 * Math.PI * 58;
  let offset = 0;

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {MOCK.kpis.map(k => (
          <KPICard key={k.key} {...k} drillable={DRILLABLE.includes(k.key)} onTap={DRILLABLE.includes(k.key) ? () => onDrill(k.key) : undefined} />
        ))}
      </div>

      <GuestVolumeTrend />

      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Hourly Check-In Volume" subtitle="Parties checked in per hour — click to drill" flex={2}>
          <div onClick={() => onDrill("checkins")} style={{ cursor: "pointer" }}>
            <DBarChart data={hData} valueKey="checkins" labelKey="h" height={160} color={T.accent} />
          </div>
        </ChartCard>
        <ChartCard title="Channel Mix" subtitle="Check-in source distribution" flex={1}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <svg width={140} height={140} viewBox="0 0 140 140" style={{ flexShrink: 0 }}>
              {chData.map((c, i) => { const pct = c.value / chTotal; const dash = circ * pct; const gap = circ - dash; const o = offset; offset += dash; return <circle key={i} cx={70} cy={70} r={58} fill="none" stroke={c.color} strokeWidth={16} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-o} transform="rotate(-90 70 70)"/>; })}
              <text x={70} y={66} textAnchor="middle" fontSize="18" fontWeight="700" fill={T.t1} fontFamily={T.font}>{chTotal}</text>
              <text x={70} y={82} textAnchor="middle" fontSize="9" fill={T.t2} fontFamily={T.font}>Parties</text>
            </svg>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {chData.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }}/>
                  <span style={{ fontSize: 11, color: T.t2, width: 72 }}>{c.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </div>

      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Party Size Distribution" subtitle="Check-ins by group size — click to drill" flex={1}>
          <div onClick={() => onDrill("avgParty")} style={{ cursor: "pointer" }}>
            <DBarChart data={pData} valueKey="count" labelKey="size" height={140} color={T.green} />
          </div>
        </ChartCard>
        <ChartCard title="Wait Time by Party Size" subtitle="Avg wait (min) — red if >30 min" flex={1}>
          <div onClick={() => onDrill("waitTime")} style={{ cursor: "pointer" }}>
            <DBarChart data={pData.map(d => ({ s: d.size, w: d.wait }))} valueKey="w" labelKey="s" height={140} color={T.orange} threshold={30} />
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Check-In Volume Heatmap" subtitle="Parties per hour × day of week — darker = higher volume" flex={1} minHeight={0}>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginRight: 8, paddingTop: 22 }}>
            {days.map(d => <div key={d} style={{ height: 20, display: "flex", alignItems: "center", fontSize: 9, color: T.t3, width: 28 }}>{d}</div>)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
              {hourLabels.map((h, i) => <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 9, color: T.t3 }}>{h}</div>)}
            </div>
            {days.map((day, di) => (
              <div key={day} style={{ display: "flex", gap: 3, marginBottom: 3 }}>
                {hours.map((_, hi) => { const v = heat[di][hi]; const intensity = v / maxHeat; return (
                  <div key={hi} style={{ flex: 1, height: 20, borderRadius: 3, background: `rgba(210,58,59,${0.06 + intensity * 0.59})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: intensity > 0.7 ? 600 : 400, color: intensity > 0.7 ? "#fff" : "transparent", cursor: "pointer" }}>{v}</div>
                ); })}
              </div>
            ))}
          </div>
        </div>
      </ChartCard>

      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Cancellation Trend" subtitle="Daily cancellations (bars) & rate % (line) — click to drill" flex={2}>
          <div onClick={() => onDrill("cancels")} style={{ cursor: "pointer" }}>
            <DBarChart data={cData.map(d => ({ d: d.d, v: d.cancels }))} valueKey="v" labelKey="d" height={120} color={T.red} />
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {cData.map((d, i) => <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 9, color: T.t3 }}>{d.rate}%</div>)}
            </div>
          </div>
        </ChartCard>
        <ChartCard title="Dine-In Duration by Party Size" subtitle="Avg minutes at table — click to drill" flex={1}>
          <div onClick={() => onDrill("dinein")} style={{ cursor: "pointer" }}>
            <DBarChart data={MOCK.dineinBySize} valueKey="duration" labelKey="size" height={120} color={T.green} />
          </div>
        </ChartCard>
      </div>

      <HourlySummaryChart />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL SCREENS
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillCheckins() {
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "This Period", value: "284" },{ label: "Prior Period", value: "262" },{ label: "Change", value: "↑ 8.3%", color: T.green },{ label: "Avg / Day", value: "41" }]}/>
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Daily Check-In Volume" subtitle="Parties per day this week" flex={2}>
          <DBarChart data={MOCK.dailyCheckins} valueKey="checkins" labelKey="d" height={160} color={T.accent} />
        </ChartCard>
        <ChartCard title="By Channel" subtitle="Check-in source breakdown" flex={1}>
          <DHProgressList data={MOCK.channels.map(c => ({ name: c.name, count: c.value }))} labelKey="name" valueKey="count" color={T.accent} />
        </ChartCard>
      </div>
      <ChartCard title="By Hour of Day" subtitle="When walk-ins arrive" flex={1} minHeight={180}>
        <DBarChart data={MOCK.hourly} valueKey="checkins" labelKey="h" height={140} color={T.accent} />
      </ChartCard>
    </div>
  );
}

function DrillGuests() {
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "This Period", value: "712" },{ label: "Prior Period", value: "678" },{ label: "Change", value: "↑ 5.1%", color: T.green },{ label: "Avg / Day", value: "102" }]}/>
      <GuestVolumeTrend height={180} />
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Daily Guest Volume" subtitle="Total guests per day" flex={2}>
          <DBarChart data={MOCK.dailyCheckins} valueKey="guests" labelKey="d" height={160} color={T.green} />
        </ChartCard>
        <ChartCard title="Guests by Party Size" subtitle="How many guests per group size" flex={1}>
          <DHProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, guests: d.guests }))} labelKey="label" valueKey="guests" color={T.green} />
        </ChartCard>
      </div>
    </div>
  );
}

function DrillAvgParty() {
  const data = MOCK.partySize;
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "Avg Party Size", value: "2.5" },{ label: "Most Common", value: "Party of 2" },{ label: "Total Parties", value: "284" }]}/>
      <ChartCard title="Party Size Distribution" subtitle="Count and percentage share by group size" flex={1} minHeight={0}>
        <DHProgressList data={data.map(d => ({ label: `Party of ${d.size}`, count: d.count, pct: `${((d.count / total) * 100).toFixed(0)}%` }))} labelKey="label" valueKey="count" color={T.green} labelWidth={100} />
      </ChartCard>
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Wait Time by Party Size" subtitle="Avg wait (min) — red if > 30 min" flex={1}>
          <DBarChart data={data.map(d => ({ s: d.size, w: d.wait }))} valueKey="w" labelKey="s" height={140} color={T.orange} threshold={30} />
        </ChartCard>
        <ChartCard title="Dine-In Duration by Party Size" subtitle="Avg minutes at table per group" flex={1}>
          <DBarChart data={data.map(d => ({ s: d.size, dur: d.dinein }))} valueKey="dur" labelKey="s" height={140} color={T.green} />
        </ChartCard>
      </div>
    </div>
  );
}

function DrillCancels() {
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "Total Cancellations", value: "35", color: T.red },{ label: "Cancel Rate", value: "12.3%" },{ label: "Change", value: "↓ 2.1%", color: T.green }]}/>
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Daily Cancellation Trend" subtitle="Cancellations per day with rate %" flex={2}>
          <DBarChart data={MOCK.cancelTrend.map(d => ({ d: d.d, v: d.cancels }))} valueKey="v" labelKey="d" height={140} color={T.red} />
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {MOCK.cancelTrend.map((d, i) => <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 9, color: T.t3 }}>{d.rate}%</div>)}
          </div>
        </ChartCard>
        <ChartCard title="By Channel" subtitle="Which channels lose the most?" flex={1}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {MOCK.cancelByChannel.map((c, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.t1 }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: T.t3, marginTop: 2 }}>Rate: {c.rate}%</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: T.red }}>{c.cancels}</div>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
      <ChartCard title="Cancellations by Hour" subtitle="When do walk-aways happen?" flex={1} minHeight={180}>
        <DBarChart data={MOCK.cancelByHour} valueKey="c" labelKey="h" height={120} color={T.red} />
      </ChartCard>
    </div>
  );
}

function DrillWaitTime() {
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "Avg Wait", value: "18 min" },{ label: "Shortest Day", value: "Mon 14m" },{ label: "Longest Day", value: "Sat 28m" }]}/>
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Wait Time by Day of Week" subtitle="Avg minutes from check-in to seating — red > 25 min" flex={1}>
          <DBarChart data={MOCK.waitByDay} valueKey="wait" labelKey="d" height={160} color={T.orange} threshold={25} />
        </ChartCard>
        <ChartCard title="Wait Time by Party Size" subtitle="Larger groups wait longer" flex={1}>
          <DHProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, wait: d.wait }))} labelKey="label" valueKey="wait" valueSuffix=" min" color={T.orange} />
        </ChartCard>
      </div>
      <CompareCards items={[
        { label: "Weekday", value: "16m", sub: "Mon–Fri average", highlight: false },
        { label: "Weekend", value: "24m", sub: "Sat–Sun average", highlight: true, softBg: T.orangeSoft, accentColor: T.orange },
      ]}/>
    </div>
  );
}

function DrillDineIn() {
  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
      <SummaryStrip items={[{ label: "Avg Duration", value: "47 min" },{ label: "Fastest Size", value: "Solo 32m" },{ label: "Slowest Size", value: "7+ at 78m" }]}/>
      <div style={{ display: "flex", gap: 16 }}>
        <ChartCard title="Duration by Day of Week" subtitle="Avg dine-in minutes per day" flex={1}>
          <DBarChart data={MOCK.dineinByDay} valueKey="dur" labelKey="d" height={160} color={T.green} />
        </ChartCard>
        <ChartCard title="Duration by Party Size" subtitle="Minutes at table per group size" flex={1}>
          <DHProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, dur: d.dinein }))} labelKey="label" valueKey="dur" valueSuffix=" min" color={T.green} />
        </ChartCard>
      </div>
      <CompareCards items={[
        { label: "Weekday", value: "44m", sub: "Mon–Fri average", highlight: false },
        { label: "Weekend", value: "52m", sub: "Sat–Sun average", highlight: true, softBg: T.greenSoft, accentColor: T.green },
      ]}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   APP CONTROLLER
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CheckinReportDesktop() {
  const [expandedGroup, setExpandedGroup] = useState("Guests");
  const [datePeriod, setDatePeriod] = useState("7 Days");
  const [drillKey, setDrillKey] = useState<any>(null);
  const goBack = () => setDrillKey(null);

  const drillMap = {
    checkins: <DrillCheckins />, guests: <DrillGuests />, avgParty: <DrillAvgParty />,
    cancels: <DrillCancels />, waitTime: <DrillWaitTime />, dinein: <DrillDineIn />,
  };

  return (
    <div style={{ display: "flex", fontFamily: T.font, background: T.bg, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap');
.material-symbols-rounded { font-family: 'Material Symbols Rounded'; font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }`}</style>

      <Sidebar expandedGroup={expandedGroup} onToggleGroup={g => setExpandedGroup(expandedGroup === g ? null : g)} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <TopBar datePeriod={datePeriod} onDatePeriodChange={setDatePeriod} drillKey={drillKey} onBack={goBack} />
        {drillKey ? drillMap[drillKey] : <MainContent onDrill={setDrillKey} />}
      </div>
    </div>
  );
}
